import React, { useEffect, useRef } from "react";

import Input from "../controls/Input";
import SelectMenu from "../controls/SelectMenu";
import { excludeAllOption } from "../../shared/filterUtils";

const FIELD_SELECT_CLASS = "w-[min(13rem,100%)]";
const LOT_DEBOUNCE_MS = 400;

interface SelectOption {
  value: number | string;
  label: string;
}

type MultiFilterKey =
  | "product"
  | "status"
  | "certification"
  | "recipe"
  | "supervisor"
  | "employee1";

interface OperationalInlineFiltersProps {
  filters: {
    product: Array<number | string>;
    status: Array<number | string>;
    lot_number: string | null;
    certification: Array<number | string>;
    recipe: Array<number | string>;
    supervisor: Array<number | string>;
    employee1: Array<number | string>;
  };
  onFilterChange: (nextFilters: OperationalInlineFiltersProps["filters"]) => void;
  onLotDraftChange: (lotNumber: string | null) => void;
  productOptions: SelectOption[];
  statusOptions: SelectOption[];
  certificationOptions: SelectOption[];
  recipeOptions: SelectOption[];
  supervisorOptions: SelectOption[];
  employeeOptions: SelectOption[];
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-[9rem] flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
        {label}
      </span>
      {children}
    </div>
  );
}

export default function OperationalInlineFilters({
  filters,
  onFilterChange,
  onLotDraftChange,
  productOptions,
  statusOptions,
  certificationOptions,
  recipeOptions,
  supervisorOptions,
  employeeOptions,
}: OperationalInlineFiltersProps) {
  const lotDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (lotDebounceRef.current) {
        clearTimeout(lotDebounceRef.current);
      }
    };
  }, []);

  const patchFilter = <K extends keyof OperationalInlineFiltersProps["filters"]>(
    key: K,
    value: OperationalInlineFiltersProps["filters"][K],
  ) => {
    onFilterChange({
      ...filters,
      [key]: value,
    });
  };

  const scheduleLotApply = (raw: string) => {
    const lotNumber = raw.trim() || null;
    onLotDraftChange(lotNumber);
    if (lotDebounceRef.current) {
      clearTimeout(lotDebounceRef.current);
    }
    lotDebounceRef.current = setTimeout(() => {
      patchFilter("lot_number", lotNumber);
    }, LOT_DEBOUNCE_MS);
  };

  const renderMultiSelect = (
    key: MultiFilterKey,
    label: string,
    options: SelectOption[],
  ) => {
    const selectableOptions = excludeAllOption(options);

    return (
      <FilterField label={label}>
        <SelectMenu
          name={key}
          options={selectableOptions}
          value={filters[key]}
          parentClass={FIELD_SELECT_CLASS}
          onValueChange={(nextValue: Array<number | string> | null) =>
            patchFilter(key, nextValue ?? [])
          }
          isMulti
          filter={selectableOptions.length > 8}
          placeholder="Select..."
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : null
          }
        />
      </FilterField>
    );
  };

  return (
    <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
      {renderMultiSelect("product", "Product", productOptions)}
      {renderMultiSelect("status", "Status", statusOptions)}
      <FilterField label="Lot number">
        <Input
          name="lot_number"
          labelOn={false}
          value={filters.lot_number ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            scheduleLotApply(e.target.value)
          }
          className="min-h-10 w-[min(11rem,100%)] rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#0F172A] shadow-sm placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#BBF7D0]"
        />
      </FilterField>
      {renderMultiSelect("certification", "Certification", certificationOptions)}
      {renderMultiSelect("recipe", "Recipe", recipeOptions)}
      {renderMultiSelect("supervisor", "Supervisor", supervisorOptions)}
      {renderMultiSelect("employee1", "Employee", employeeOptions)}
    </div>
  );
}
